import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Search, X, ChevronDown, ChevronUp, ArrowRight,
  Info, Loader, Plus, Minus, AlertTriangle,
  RefreshCw, TrendingUp, TrendingDown, Zap, Share2,
} from 'lucide-react';
import RiskCalcShareCard from './RiskCalcShareCard';
import { captureAndShare } from '../utils/shareImage';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const inr = (n) =>
  n == null || isNaN(n) ? '—'
  : `₹${Math.abs(Number(n)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const num = (n, d = 0) =>
  n == null || isNaN(n) ? '—'
  : Number(n).toLocaleString('en-IN', { maximumFractionDigits: d });
const pct = (n, d = 1) =>
  n == null || isNaN(n) ? '—' : `${Number(n).toFixed(d)}%`;
const estimateBrokerage = (v) => Math.max(40, v * 0.001);

// -- Allocation helpers --------------------------------------------------------
function buildAllocs(n) {
  if (n <= 0) return [];
  const base = Math.floor(100 / n);
  const rem  = 100 - base * n;
  return Array.from({ length: n }, (_, i) => i === 0 ? base + rem : base);
}

function applyAllocDelta(prev, idx, delta) {
  const next = [...prev];
  const proposed = Math.max(5, Math.min(90, next[idx] + delta));
  const actualDelta = proposed - next[idx];
  if (actualDelta === 0) return prev;
  const compensate = -actualDelta;
  for (let attempt = next.length - 1; attempt >= 0; attempt--) {
    if (attempt === idx) continue;
    const newV = next[attempt] + compensate;
    if (newV >= 5 && newV <= 90) {
      next[idx] = proposed;
      next[attempt] = newV;
      return next;
    }
  }
  return prev;
}

// -- Stock search --------------------------------------------------------------
function StockSearch({ value, onChange, onSelect, colors }) {
  const [list,     setList]     = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [fetching, setFetching] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/stocks`).then(r => setList(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!value?.trim()) { setFiltered([]); setShowDrop(false); return; }
    const q = value.toUpperCase();
    const res = list.filter(s =>
      s.symbol?.replace('.NS','').includes(q) || s.company_name?.toUpperCase().includes(q)
    ).slice(0, 7);
    setFiltered(res);
    setShowDrop(res.length > 0);
  }, [value, list]);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const pick = useCallback(async (stock) => {
    const sym = stock.symbol.replace('.NS', '');
    onChange(sym); setShowDrop(false); setFetching(true);
    try {
      const r = await axios.get(`${API_URL}/stocks/${sym}`);
      const price = r.data?.current_price || r.data?.live_price || 0;
      onSelect({ symbol: sym, company: stock.company_name, price: price > 0 ? price : null });
    } catch (_) { onSelect({ symbol: sym, company: stock.company_name, price: null }); }
    finally { setFetching(false); }
  }, [onChange, onSelect]);

  return (
    <div ref={ref} className="relative">
      <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg"
        style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
        {fetching
          ? <Loader className="w-3 h-3 animate-spin flex-shrink-0" style={{ color: colors.accent }} />
          : <Search className="w-3 h-3 flex-shrink-0" style={{ color: colors.secondaryText }} />}
        <input className="flex-1 bg-transparent text-xs outline-none" style={{ color: colors.primaryText }}
          placeholder="Stock name or symbol" value={value}
          onChange={e => { onChange(e.target.value.toUpperCase()); onSelect(null); }}
          onFocus={() => filtered.length > 0 && setShowDrop(true)} />
        {value && <button onMouseDown={() => { onChange(''); onSelect(null); }}>
          <X className="w-3 h-3" style={{ color: colors.secondaryText }} /></button>}
      </div>
      {showDrop && filtered.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 rounded-lg overflow-hidden z-50 shadow-xl"
          style={{ backgroundColor: colors.cardBg || colors.inputBg, border: `1px solid ${colors.border}` }}>
          {filtered.map(s => (
            <button key={s.symbol} onMouseDown={() => pick(s)}
              className="w-full flex flex-col px-3 py-2 text-left hover:opacity-75 transition-opacity"
              style={{ backgroundColor: colors.cardBg || colors.inputBg }}>
              <span className="text-xs font-semibold" style={{ color: colors.primaryText }}>
                {s.symbol.replace('.NS', '')}
              </span>
              <span className="text-[13px]" style={{ color: colors.secondaryText }}>
                {s.company_name?.substring(0, 32)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// -- Stepper -------------------------------------------------------------------
function Stepper({ value, onStep, step = 0.5, min = 0.5, max = 50, color }) {
  const v = parseFloat(value) || 0;
  return (
    <div className="flex items-center rounded-2xl overflow-hidden"
      style={{ backgroundColor: `${color}12`, border: `1px solid ${color}25` }}>
      <button
        onMouseDown={e => { e.preventDefault(); onStep(Math.max(min, parseFloat((v - step).toFixed(1))).toString()); }}
        className="flex items-center justify-center flex-shrink-0 transition-opacity active:opacity-60"
        style={{ width: 36, height: 36, color }}>
        <Minus className="w-3.5 h-3.5" />
      </button>
      <input type="number" step={step} min={min} max={max}
        className="flex-1 bg-transparent text-sm outline-none font-black text-center tabular-nums"
        style={{ color, minWidth: 0 }} value={value}
        onChange={e => onStep(e.target.value)} />
      <button
        onMouseDown={e => { e.preventDefault(); onStep(Math.min(max, parseFloat((v + step).toFixed(1))).toString()); }}
        className="flex items-center justify-center flex-shrink-0 transition-opacity active:opacity-60"
        style={{ width: 36, height: 36, color }}>
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// -- F&O constants ------------------------------------------------------------
const FO_UNDERLYINGS = [
  { key: 'NIFTY50',   label: 'NIFTY',      lotSize: 75 },
  { key: 'BANKNIFTY', label: 'BANK NIFTY', lotSize: 35 },
];
const ZONE_COLOR = { ATM: '#8b5cf6', ITM: '#10b981', OTM: '#f59e0b' };

// -- Trade slot (Equity + F&O unified) ----------------------------------------
function TradeSlot({ index, slotCapital, riskBudget, colors, navigate, onResult, initialSlotState, onSlotStateChange }) {
  const { token } = useAuth();
  const s = initialSlotState || {};
  const [open,      setOpen]      = useState(index === 0);
  const [tradeType, setTradeType] = useState(s.tradeType ?? 'equity');

  // -- Equity state ----------------------------------------------------------
  const [query,          setQuery]          = useState(s.query          ?? '');
  const [stock,          setStock]          = useState(s.stock          ?? null);
  const [entry,          setEntry]          = useState(s.entry          ?? '');
  const [slPct,          setSlPct]          = useState(s.slPct          ?? '2');
  const [targetPct,      setTargetPct]      = useState(s.targetPct      ?? '4');
  const [sharesOverride, setSharesOverride] = useState(s.sharesOverride ?? null);

  // -- F&O state -------------------------------------------------------------
  const [underlying,     setUnderlying]     = useState(s.underlying     ?? 'NIFTY50');
  const [instType,       setInstType]       = useState(s.instType       ?? 'options');
  const [direction,      setDirection]      = useState(s.direction      ?? 'BUY');
  const [optType,        setOptType]        = useState(s.optType        ?? 'CE');
  const [lots,           setLots]           = useState(s.lots           ?? 1);
  const [foEntry,        setFoEntry]        = useState(s.foEntry        ?? '');
  const [foSl,           setFoSl]           = useState(s.foSl           ?? '');
  const [foTarget,       setFoTarget]       = useState(s.foTarget       ?? '');
  const [selectedStrike, setSelectedStrike] = useState(null); // strike not persisted (chain data)

  // Persist slot state whenever anything changes
  useEffect(() => {
    onSlotStateChange?.(index, { tradeType, query, stock, entry, slPct, targetPct, sharesOverride, underlying, instType, direction, optType, lots, foEntry, foSl, foTarget });
  }, [tradeType, query, stock, entry, slPct, targetPct, sharesOverride, underlying, instType, direction, optType, lots, foEntry, foSl, foTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  const isEq = tradeType === 'equity';

  // -- Equity derived --------------------------------------------------------
  useEffect(() => { if (stock?.price) setEntry(String(stock.price)); }, [stock]);
  useEffect(() => { setSharesOverride(null); }, [entry]);

  const entryN       = parseFloat(entry)     || 0;
  const slPctN       = parseFloat(slPct)     || 0;
  const tgtPctN      = parseFloat(targetPct) || 0;
  const defaultShares   = entryN > 0 ? Math.floor(slotCapital / entryN) : 0;
  const shares          = sharesOverride !== null ? sharesOverride : defaultShares;
  const eqCapitalUsed   = shares * entryN;
  const slPrice         = (entryN > 0 && slPctN > 0) ? entryN * (1 - slPctN / 100) : 0;
  const targetPrice     = (entryN > 0 && tgtPctN > 0) ? entryN * (1 + tgtPctN / 100) : 0;
  const lossPerShare    = slPrice > 0 ? entryN - slPrice : 0;
  const eqActualMaxLoss = lossPerShare > 0 ? shares * lossPerShare : 0;
  const eqMaxProfit     = targetPrice > 0 ? shares * (targetPrice - entryN) : 0;
  const eqBrokerage     = eqCapitalUsed > 0 ? estimateBrokerage(eqCapitalUsed) : 0;
  const eqNetProfit     = eqMaxProfit > 0 ? eqMaxProfit - eqBrokerage : 0;
  const eqNetLoss       = eqActualMaxLoss > 0 ? eqActualMaxLoss + eqBrokerage : 0;
  const eqRR            = (slPctN > 0 && tgtPctN > 0) ? (tgtPctN / slPctN).toFixed(1) : null;
  const hasSL           = slPctN > 0 && entryN > 0;
  const hasTarget       = tgtPctN > 0 && entryN > 0;
  const eqReady         = shares > 0 && entryN > 0;

  // -- F&O derived -----------------------------------------------------------
  const uData      = FO_UNDERLYINGS.find(u => u.key === underlying);
  const lotSize    = uData.lotSize;
  const totalUnits = lots * lotSize;
  const foEntryN   = parseFloat(foEntry)  || 0;
  const foSlN      = parseFloat(foSl)     || 0;
  const foTargetN  = parseFloat(foTarget) || 0;
  const foReady    = lots > 0 && foEntryN > 0;

  useEffect(() => {
    if (!selectedStrike) return;
    const price = optType === 'CE'
      ? (selectedStrike.callLtp ?? selectedStrike.call?.ltp)
      : (selectedStrike.putLtp  ?? selectedStrike.put?.ltp);
    if (price != null) setFoEntry(String(price));
  }, [optType, selectedStrike]);

  const handleStrikeSelect = useCallback((row) => {
    setSelectedStrike(row);
    if (row) {
      const price = optType === 'CE'
        ? (row.callLtp ?? row.call?.ltp)
        : (row.putLtp  ?? row.put?.ltp);
      if (price != null) setFoEntry(String(price));
    } else { setFoEntry(''); }
  }, [optType]);

  let foMaxProfit = 0, foMaxLoss = 0, foMargin = 0;
  if (instType === 'options') {
    if (direction === 'BUY') {
      foMaxLoss   = foSlN > 0 ? (foEntryN - foSlN) * totalUnits : foEntryN * totalUnits;
      foMaxProfit = foTargetN > 0 ? (foTargetN - foEntryN) * totalUnits : 0;
      foMargin    = foEntryN * totalUnits;
    } else {
      foMaxProfit = foTargetN > 0 ? (foEntryN - foTargetN) * totalUnits : foEntryN * totalUnits;
      foMaxLoss   = foSlN > 0 ? (foSlN - foEntryN) * totalUnits : 0;
      foMargin    = foEntryN * totalUnits * 3;
    }
  } else {
    if (direction === 'LONG') {
      foMaxProfit = foTargetN > 0 ? (foTargetN - foEntryN) * totalUnits : 0;
      foMaxLoss   = foSlN > 0    ? (foEntryN - foSlN) * totalUnits      : 0;
    } else {
      foMaxProfit = foTargetN > 0 ? (foEntryN - foTargetN) * totalUnits : 0;
      foMaxLoss   = foSlN > 0    ? (foSlN - foEntryN) * totalUnits      : 0;
    }
    foMargin = foEntryN * totalUnits * 0.1;
  }
  const foBrokerage = foReady ? 40 : 0;
  const foNetProfit = foMaxProfit > 0 ? foMaxProfit - foBrokerage : 0;
  const foNetLoss   = foMaxLoss   > 0 ? foMaxLoss   + foBrokerage : 0;
  const foRR        = foMaxLoss > 0 && foMaxProfit > 0 ? (foMaxProfit / foMaxLoss).toFixed(1) : null;
  const foWorstCase = instType === 'options' && direction === 'BUY' ? foEntryN * totalUnits : foMaxLoss;
  const foHasSL     = foSlN > 0;
  const foHasTarget = foTargetN > 0;

  // -- Header labels (needed by onResult below) ------------------------------
  const tradeLabel = isEq
    ? (stock?.symbol || `Trade ${index + 1}`)
    : (foReady ? `${uData.label} ${instType === 'options' ? `${direction} ${optType}` : `Fut ${direction}`}` : `Trade ${index + 1}`);

  // -- Unified result reporting -----------------------------------------------
  const ready = isEq ? eqReady : foReady;
  useEffect(() => {
    onResult(index, ready ? {
      capitalUsed:   isEq ? eqCapitalUsed : foMargin,
      actualMaxLoss: isEq ? (hasSL    ? eqActualMaxLoss : 0) : (foHasSL    ? foMaxLoss   : 0),
      maxProfit:     isEq ? (hasTarget ? eqMaxProfit    : 0) : (foHasTarget ? foMaxProfit : 0),
      netProfit:     isEq ? (hasTarget ? eqNetProfit    : 0) : (foHasTarget ? foNetProfit : 0),
      netLoss:       isEq ? (hasSL    ? eqNetLoss       : 0) : (foHasSL    ? foNetLoss   : 0),
      brokerage:     isEq ? eqBrokerage : foBrokerage,
      shares:        isEq ? shares : totalUnits,
      label:         tradeLabel,
      entry:         isEq ? entryN : foEntryN,
      sl:            isEq ? slPrice : foSlN,
      target:        isEq ? targetPrice : foTargetN,
      rr:            isEq ? eqRR : foRR,
      units:         isEq ? shares : totalUnits,
      isEq,
    } : null);
  }, [index, ready, isEq,
      eqCapitalUsed, eqActualMaxLoss, eqMaxProfit, eqNetProfit, eqNetLoss, eqBrokerage, shares, hasSL, hasTarget,
      foMargin, foMaxLoss, foMaxProfit, foNetProfit, foNetLoss, foBrokerage, totalUnits, foHasSL, foHasTarget,
      tradeLabel, entryN, foEntryN, slPrice, foSlN, targetPrice, foTargetN, eqRR, foRR,
      onResult]);

  // -- Shared warnings -------------------------------------------------------
  const capitalUsedForSlot = isEq ? eqCapitalUsed : foMargin;
  const capitalOverSlot    = capitalUsedForSlot > slotCapital && capitalUsedForSlot > 0;
  const actualLossForWarn  = isEq ? eqActualMaxLoss : foMaxLoss;
  const lossOverBudget     = actualLossForWarn > 0 && actualLossForWarn > riskBudget;
  const currentRR          = isEq ? eqRR : foRR;
  const poorRR             = currentRR && parseFloat(currentRR) < 1.5;
  const sharesModified     = isEq && sharesOverride !== null && sharesOverride !== defaultShares;
  const rrColor            = currentRR
    ? parseFloat(currentRR) >= 2 ? '#16C784' : parseFloat(currentRR) >= 1.5 ? '#F59E0B' : '#EF4444'
    : colors.secondaryText;

  // -- Header labels ---------------------------------------------------------
  const tradeSubLabel = isEq
    ? (eqReady ? `${num(shares)} shares — ${inr(eqCapitalUsed)} deployed` : `${inr(slotCapital)} allocated — add stock`)
    : (foReady ? `${lots} lot${lots > 1 ? 's' : ''} — ${inr(foMargin)} margin` : `${inr(slotCapital)} allocated — configure F&O`);
  const showPnL       = isEq ? (eqReady && hasSL && hasTarget) : (foReady && foHasSL && foHasTarget);
  const headerProfit  = isEq ? eqNetProfit : foNetProfit;
  const headerLoss    = isEq ? eqNetLoss   : foNetLoss;

  const tabStyle = (active, color) => active
    ? { background: color || colors.accent, color: '#fff' }
    : { background: colors.inputBg, color: colors.secondaryText, border: `1px solid ${colors.border}` };

  return (
    <div className="rounded-xl overflow-hidden"
      style={{ border: `1px solid ${capitalOverSlot ? '#F59E0B40' : ready ? '#6366F125' : colors.border}` }}>

      {/* Header */}
      <button className="app-card w-full flex items-center justify-between p-3"
        style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}
        onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[13px] font-bold flex-shrink-0"
            style={{ backgroundColor: ready ? '#6366F118' : colors.inputBg, color: ready ? '#6366F1' : colors.secondaryText }}>
            {index + 1}
          </div>
          <div className="text-left">
            <div className="flex items-center gap-1.5">
              <p className="text-xs font-semibold" style={{ color: colors.primaryText }}>{tradeLabel}</p>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                style={{ backgroundColor: isEq ? `${colors.accent}20` : '#f59e0b20',
                         color: isEq ? colors.accent : '#f59e0b' }}>
                {isEq ? 'EQ' : 'F&O'}
              </span>
            </div>
            <p className="text-[13px]" style={{ color: colors.secondaryText }}>{tradeSubLabel}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {showPnL && (
            <div className="text-right">
              <p className="text-[13px] font-bold" style={{ color: '#16C784' }}>+{inr(headerProfit)}</p>
              <p className="text-[13px] font-bold" style={{ color: '#EF4444' }}>-{inr(headerLoss)}</p>
            </div>
          )}
          {open ? <ChevronUp className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
                : <ChevronDown className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />}
        </div>
      </button>

      {/* Body */}
      {open && (
        <div className="p-3 pt-0 space-y-3 border-t"
          style={{ borderColor: colors.border, backgroundColor: `${colors.inputBg}60` }}>

          {/* Type toggle + budget in one row */}
          <div className="flex items-center gap-2 mt-2">
            <div className="flex gap-1.5">
              {[{ id: 'equity', label: 'Equity' }, { id: 'fo', label: 'F&O' }].map(t => (
                <button key={t.id} onClick={() => setTradeType(t.id)}
                  className="py-1 px-3 rounded-lg text-[11px] font-bold"
                  style={tabStyle(tradeType === t.id)}>{t.label}</button>
              ))}
            </div>
            <div className="flex-1 flex items-center gap-1.5 px-2 py-1 rounded-lg"
              style={{ backgroundColor: `${colors.accent}08`, border: `1px solid ${colors.accent}15` }}>
              <Info className="w-2.5 h-2.5 flex-shrink-0" style={{ color: colors.accent }} />
              <p className="text-[10px]" style={{ color: colors.secondaryText }}>
                <span className="font-semibold" style={{ color: colors.accent }}>{inr(slotCapital)}</span>
                {' '}alloc — <span className="font-semibold" style={{ color: '#EF4444' }}>{inr(riskBudget)}</span> risk
              </p>
            </div>
          </div>

          {/* -- Equity body -- */}
          {isEq && (
            <>
              <StockSearch value={query} onChange={setQuery}
                onSelect={s => { setStock(s); if (!s) setEntry(''); }} colors={colors} />
              {stock?.price > 0 && <p className="text-[13px]" style={{ color: '#16C784' }}>Live: {inr(stock.price)}</p>}
              <div>
                <label className="text-[13px] mb-1 block" style={{ color: colors.secondaryText }}>Entry Price (₹)</label>
                <input type="number" value={entry} onChange={e => setEntry(e.target.value)}
                  className="w-full px-2.5 py-2 rounded-lg text-xs outline-none"
                  style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}`, color: colors.primaryText }}
                  placeholder="Auto-filled from live price" />
              </div>
              {entryN > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[13px]" style={{ color: colors.secondaryText }}>Shares</label>
                    <div className="flex items-center gap-2">
                      {sharesModified && (
                        <button onClick={() => setSharesOverride(null)}
                          className="flex items-center gap-1 text-[12px]" style={{ color: colors.accent }}>
                          <RefreshCw className="w-2.5 h-2.5" /> Reset ({defaultShares})
                        </button>
                      )}
                      <span className="text-[12px]" style={{ color: sharesModified ? '#F59E0B' : colors.secondaryText }}>
                        {sharesModified ? 'manually set' : 'default from allocation'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 px-3 py-2 rounded-xl"
                    style={{ backgroundColor: colors.inputBg, border: `1px solid ${sharesModified ? '#F59E0B40' : colors.border}` }}>
                    <button onClick={() => setSharesOverride(s => Math.max(1, (s ?? defaultShares) - 1))}
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${colors.accent}15` }}>
                      <Minus className="w-3 h-3" style={{ color: colors.primaryText }} />
                    </button>
                    <div className="flex-1 text-center">
                      <span className="text-xl font-black" style={{ color: sharesModified ? '#F59E0B' : colors.accent }}>{shares}</span>
                      <span className="text-xs ml-1" style={{ color: colors.secondaryText }}>shares</span>
                    </div>
                    <button onClick={() => setSharesOverride(s => (s ?? defaultShares) + 1)}
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${colors.accent}15` }}>
                      <Plus className="w-3 h-3" style={{ color: colors.primaryText }} />
                    </button>
                  </div>
                  <div className="flex justify-between mt-1.5 px-1">
                    <span className="text-[12px]" style={{ color: colors.secondaryText }}>
                      Deployed: <span className="font-semibold" style={{ color: colors.primaryText }}>{inr(eqCapitalUsed)}</span>
                    </span>
                    <span className="text-[12px]" style={{ color: capitalOverSlot ? '#F59E0B' : colors.secondaryText }}>
                      Allocated: {inr(slotCapital)}
                    </span>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid #EF444430', backgroundColor: '#EF444408' }}>
                  <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#EF4444' }}>Stop Loss</span>
                    <span className="text-[11px] font-bold" style={{ color: '#EF4444' }}>{hasSL ? inr(slPrice) : 'optional'}</span>
                  </div>
                  <div className="px-2 pb-2">
                    <Stepper value={slPct} onStep={setSlPct} step={0.5} min={0.5} max={20} color="#EF4444" />
                    <p className="text-center text-[10px] mt-1" style={{ color: '#EF444480' }}>% below entry</p>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid #16C78430', backgroundColor: '#16C78408' }}>
                  <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#16C784' }}>Target</span>
                    <span className="text-[11px] font-bold" style={{ color: '#16C784' }}>{hasTarget ? inr(targetPrice) : 'optional'}</span>
                  </div>
                  <div className="px-2 pb-2">
                    <Stepper value={targetPct} onStep={setTargetPct} step={0.5} min={0.5} max={50} color="#16C784" />
                    <p className="text-center text-[10px] mt-1" style={{ color: '#16C78480' }}>% above entry</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* -- F&O body -- */}
          {!isEq && (
            <>
              {/* Row 1: underlying | instrument type */}
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-widest mb-1" style={{ color: colors.secondaryText, opacity: 0.5 }}>Underlying & Type</p>
                <div className="flex items-center gap-2">
                  {/* Underlying */}
                  <div className="flex flex-1 gap-1 p-0.5 rounded-lg" style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
                    {FO_UNDERLYINGS.map(u => (
                      <button key={u.key}
                        onClick={() => { setUnderlying(u.key); setSelectedStrike(null); setFoEntry(''); }}
                        className="flex-1 py-1.5 rounded-md text-[11px] font-bold whitespace-nowrap"
                        style={tabStyle(underlying === u.key)}>
                        {u.label}
                        <span className="font-normal opacity-60 text-[10px] ml-0.5">×{u.lotSize}</span>
                      </button>
                    ))}
                  </div>
                  {/* Vertical divider */}
                  <div className="h-6 w-px flex-shrink-0" style={{ backgroundColor: colors.border }} />
                  {/* Instrument type */}
                  <div className="flex gap-1 p-0.5 rounded-lg" style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
                    {[{ id: 'options', label: 'Opts' }, { id: 'futures', label: 'Fut' }].map(t => (
                      <button key={t.id}
                        onClick={() => { setInstType(t.id); setFoSl(''); setFoTarget(''); setDirection(t.id === 'futures' ? 'LONG' : 'BUY'); setSelectedStrike(null); setFoEntry(''); }}
                        className="px-3 py-1.5 rounded-md text-[11px] font-bold"
                        style={tabStyle(instType === t.id)}>{t.label}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="w-full h-px" style={{ backgroundColor: colors.border, opacity: 0.5 }} />

              {/* Row 2: direction + CE/PE + lots */}
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-widest mb-1" style={{ color: colors.secondaryText, opacity: 0.5 }}>Direction & Quantity</p>
                <div className="flex items-center gap-1.5 flex-wrap">
                {instType === 'options' ? (
                  <>
                    {/* Direction group */}
                    <div className="flex gap-1 p-0.5 rounded-lg" style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
                      {[{ id: 'BUY', color: '#16C784' }, { id: 'SELL', color: '#EF4444' }].map(d => (
                        <button key={d.id} onClick={() => setDirection(d.id)}
                          className="py-1.5 px-3 rounded-md text-[11px] font-bold"
                          style={tabStyle(direction === d.id, d.color)}>{d.id}</button>
                      ))}
                    </div>
                    {/* Vertical divider */}
                    <div className="h-6 w-px flex-shrink-0" style={{ backgroundColor: colors.border }} />
                    {/* Option type group */}
                    <div className="flex gap-1 p-0.5 rounded-lg" style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
                      {['CE', 'PE'].map(t => (
                        <button key={t} onClick={() => setOptType(t)}
                          className="py-1.5 px-3 rounded-md text-[11px] font-bold"
                          style={tabStyle(optType === t, '#8b5cf6')}>{t}</button>
                      ))}
                    </div>
                  </>
                ) : (
                  [{ id: 'LONG', color: '#16C784' }, { id: 'SHORT', color: '#EF4444' }].map(d => (
                    <button key={d.id} onClick={() => setDirection(d.id)}
                      className="py-1.5 px-3 rounded-lg text-[11px] font-bold"
                      style={tabStyle(direction === d.id, d.color)}>{d.id}</button>
                  ))
                )}
                {/* Compact lots stepper */}
                <div className="flex items-center gap-1 px-2 py-1 rounded-lg"
                  style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
                  <button onClick={() => setLots(l => Math.max(1, l - 1))}
                    className="w-5 h-5 flex items-center justify-center rounded"
                    style={{ backgroundColor: `${colors.accent}20` }}>
                    <Minus className="w-2.5 h-2.5" style={{ color: colors.accent }} />
                  </button>
                  <span className="text-xs font-black px-1.5 min-w-[1.4rem] text-center"
                    style={{ color: colors.accent }}>{lots}</span>
                  <button onClick={() => setLots(l => Math.min(50, l + 1))}
                    className="w-5 h-5 flex items-center justify-center rounded"
                    style={{ backgroundColor: `${colors.accent}20` }}>
                    <Plus className="w-2.5 h-2.5" style={{ color: colors.accent }} />
                  </button>
                  <span className="text-[10px] ml-1 whitespace-nowrap"
                    style={{ color: colors.secondaryText }}>
                    lot{lots > 1 ? 's' : ''} · {totalUnits}u
                  </span>
                </div>
                </div>
              </div>

              <div className="w-full h-px" style={{ backgroundColor: colors.border, opacity: 0.5 }} />

              {/* Strike search (options only) */}
              {instType === 'options' && (
                <StrikeSearch underlying={underlying} optType={optType}
                  onSelect={handleStrikeSelect} colors={colors} token={token} />
              )}

              {/* Premium / SL / Target — compact 3-column */}
              <div className="grid grid-cols-3 gap-1.5">
                <div>
                  <label className="text-[10px] mb-0.5 block font-medium" style={{ color: colors.secondaryText }}>
                    {instType === 'options' ? 'Premium ₹' : 'Entry ₹'}
                    {foEntry ? <span style={{ color: colors.accent }}> ₹</span> : ''}
                  </label>
                  <input type="number" value={foEntry}
                    onChange={e => { setFoEntry(e.target.value); if (selectedStrike) setSelectedStrike(null); }}
                    className="w-full px-2 py-1.5 rounded-lg text-xs outline-none font-semibold"
                    style={{ backgroundColor: colors.inputBg,
                             border: `1px solid ${foEntry ? colors.accent + '50' : colors.border}`,
                             color: colors.primaryText }}
                    placeholder="0.00" />
                </div>
                <div>
                  <label className="text-[10px] mb-0.5 block font-medium" style={{ color: '#EF4444' }}>SL ₹</label>
                  <input type="number" value={foSl} onChange={e => setFoSl(e.target.value)}
                    className="w-full px-2 py-1.5 rounded-lg text-xs outline-none font-semibold"
                    style={{ backgroundColor: colors.inputBg, border: '1px solid #EF444430', color: '#EF4444' }}
                    placeholder="—" />
                </div>
                <div>
                  <label className="text-[10px] mb-0.5 block font-medium" style={{ color: '#16C784' }}>Target ₹</label>
                  <input type="number" value={foTarget} onChange={e => setFoTarget(e.target.value)}
                    className="w-full px-2 py-1.5 rounded-lg text-xs outline-none font-semibold"
                    style={{ backgroundColor: colors.inputBg, border: '1px solid #16C78430', color: '#16C784' }}
                    placeholder="—" />
                </div>
              </div>

              {foReady && instType === 'options' && direction === 'SELL' && !foSlN && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
                  style={{ backgroundColor: '#EF444412', border: '1px solid #EF444430' }}>
                  <AlertTriangle className="w-3 h-3 flex-shrink-0" style={{ color: '#EF4444' }} />
                  <p className="text-[11px]" style={{ color: '#EF4444' }}>
                    SELL options — unlimited downside, set a SL premium
                  </p>
                </div>
              )}
            </>
          )}

          {/* -- Shared warnings -- */}
          {capitalOverSlot && (
            <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg"
              style={{ backgroundColor: '#F59E0B12', border: '1px solid #F59E0B30' }}>
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#F59E0B' }} />
              <p className="text-[13px]" style={{ color: '#F59E0B' }}>
                {inr(capitalUsedForSlot)} {isEq ? 'deployed' : 'margin'} — {inr(capitalUsedForSlot - slotCapital)} over your {inr(slotCapital)} allocation
              </p>
            </div>
          )}
          {lossOverBudget && (
            <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg"
              style={{ backgroundColor: '#EF444412', border: '1px solid #EF444430' }}>
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#EF4444' }} />
              <p className="text-[13px]" style={{ color: '#EF4444' }}>
                Max loss {inr(actualLossForWarn)} exceeds risk budget {inr(riskBudget)} — tighten SL or reduce {isEq ? 'shares' : 'lots'}
              </p>
            </div>
          )}
          {poorRR && (
            <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg"
              style={{ backgroundColor: '#F59E0B12', border: '1px solid #F59E0B30' }}>
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#F59E0B' }} />
              <p className="text-[13px]" style={{ color: '#F59E0B' }}>
                R:R of 1:{currentRR} is below 1:1.5 — reward doesn't justify the risk
              </p>
            </div>
          )}

          {/* -- Output -- */}
          {ready && (
            <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>
              {isEq && hasSL && hasTarget && (
                <div className="grid grid-cols-2">
                  <div className="p-2.5 text-center" style={{ backgroundColor: '#16C78408', borderRight: `1px solid ${colors.border}` }}>
                    <p className="text-[12px] mb-0.5" style={{ color: colors.secondaryText }}>Max Profit</p>
                    <p className="text-sm font-black" style={{ color: '#16C784' }}>+{inr(eqNetProfit)}</p>
                    <p className="text-[12px]" style={{ color: rrColor }}>R:R 1:{eqRR}</p>
                  </div>
                  <div className="p-2.5 text-center" style={{ backgroundColor: '#EF444408' }}>
                    <p className="text-[12px] mb-0.5" style={{ color: colors.secondaryText }}>Max Loss (if SL hit)</p>
                    <p className="text-sm font-black" style={{ color: '#EF4444' }}>-{inr(eqNetLoss)}</p>
                    <p className="text-[12px]" style={{ color: lossOverBudget ? '#EF4444' : colors.secondaryText }}>
                      {lossOverBudget ? '⚠ over budget' : ' within budget'}
                    </p>
                  </div>
                </div>
              )}
              {!isEq && (foMaxProfit > 0 || foWorstCase > 0) && (
                <div className="grid grid-cols-2">
                  <div className="p-2.5 text-center" style={{ backgroundColor: '#16C78408', borderRight: `1px solid ${colors.border}` }}>
                    <p className="text-[12px] mb-0.5" style={{ color: colors.secondaryText }}>Max Profit</p>
                    <p className="text-sm font-black" style={{ color: '#16C784' }}>
                      {foMaxProfit > 0 ? `+${inr(foNetProfit)}` : '…'}
                    </p>
                    {foRR && <p className="text-[12px]" style={{ color: rrColor }}>R:R 1:{foRR}</p>}
                  </div>
                  <div className="p-2.5 text-center" style={{ backgroundColor: '#EF444408' }}>
                    <p className="text-[12px] mb-0.5" style={{ color: colors.secondaryText }}>Max Loss</p>
                    <p className="text-sm font-black" style={{ color: '#EF4444' }}>
                      {instType === 'options' && direction === 'SELL' && !foSlN
                        ? 'Unlimited ⚠'
                        : `-${inr(foMaxLoss > 0 ? foNetLoss : foWorstCase + foBrokerage)}`}
                    </p>
                  </div>
                </div>
              )}
              <div className="app-card px-3 py-2.5 space-y-2"
                style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
                {(isEq ? [
                  ['Shares',           `${num(shares)} shares`,    sharesModified ? '✎ manually adjusted' : null],
                  ['Capital deployed', inr(eqCapitalUsed),         'money entering the trade'],
                  hasSL    && ['Max loss (SL)',   `~${inr(eqActualMaxLoss)}`, `${num(shares)} — ₹${lossPerShare.toFixed(1)}/share`],
                  hasSL    && ['SL price',        `${inr(slPrice)} (${slPctN}% below)`, null],
                  hasTarget && ['Target price',   `${inr(targetPrice)} (${tgtPctN}% above)`, null],
                  ['Brokerage + taxes', `~${inr(eqBrokerage)}`, '0.1% est.'],
                ] : [
                  ['Instrument',      `${uData.label} ${instType === 'options' ? `${direction} ${optType}` : `Fut ${direction}`}`, null],
                  ['Lots — Lot Size', `${lots} — ${lotSize} = ${totalUnits} units`, null],
                  foEntryN > 0  && ['Entry',        `₹${foEntryN}`,   instType === 'options' ? 'premium/unit' : 'price/unit'],
                  foSlN > 0     && ['SL',            `₹${foSlN}`,      `loss: ₹${Math.abs(foSlN - foEntryN).toFixed(1)}/unit`],
                  foTargetN > 0 && ['Target',        `₹${foTargetN}`,  `profit: ₹${Math.abs(foTargetN - foEntryN).toFixed(1)}/unit`],
                  foReady       && ['Margin (est.)', inr(foMargin),    instType === 'futures' ? '~10% of notional' : direction === 'BUY' ? 'premium paid' : 'SPAN margin'],
                  foReady       && ['Brokerage',     '~₹40',           '₹20 per leg'],
                ]).filter(Boolean).map(([label, value, note]) => (
                  <div key={label} className="flex items-baseline justify-between gap-2">
                    <span className="text-[11px] flex-shrink-0" style={{ color: colors.secondaryText }}>
                      {label}
                      {note && <span className="ml-1 text-[11px]" style={{ color: note.startsWith('₹') ? '#F59E0B' : colors.secondaryText }}>({note})</span>}
                    </span>
                    <span className="text-xs font-semibold" style={{ color: colors.primaryText }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ready && isEq && stock?.symbol && (
            <button onClick={() => navigate('/trade', {
                state: { symbol: stock.symbol, entry: entryN, stopLoss: slPrice || undefined,
                         target: targetPrice || undefined, quantity: shares } })}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold"
              style={{ backgroundColor: colors.accent, color: '#fff' }}>
              Place this trade <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
          {ready && !isEq && (
            <button onClick={() => navigate('/derivatives')}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold"
              style={{ backgroundColor: colors.accent, color: '#fff' }}>
              Go to F&O Desk <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
          {!ready && (
            <p className="text-[13px] text-center py-1" style={{ color: colors.secondaryText }}>
              {isEq ? 'Search a stock and enter price to plan'
                : instType === 'options' ? 'Pick a strike or enter premium to see P&L'
                : 'Enter price to see P&L'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// -- Strike dropdown (like StockSearch) ---------------------------------------
const fmtExp = (iso) => {
  if (!iso) return iso;
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const normalizeChainData = (raw) => {
  if (!raw?.chain) return null;
  const spot = raw.spot;
  return {
    ...raw,
    chain: raw.chain.map(row => ({
      ...row,
      callLtp: row.call?.ltp ?? null,
      putLtp:  row.put?.ltp  ?? null,
      zone: row.is_atm ? 'ATM' : (spot && row.strike < spot ? 'ITM' : 'OTM'),
    })),
  };
};

function StrikeSearch({ underlying, optType, onSelect, colors, token }) {
  const [query,          setQuery]          = useState('');
  const [chain,          setChain]          = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [showDrop,       setShowDrop]       = useState(false);
  const [selected,       setSelected]       = useState(null);
  const [expiries,       setExpiries]       = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState(null);
  const [defaultExpiry,  setDefaultExpiry]  = useState(null);
  const ref = useRef(null);

  // Bootstrap: runs on underlying change — loads expiry list + default chain
  useEffect(() => {
    setSelected(null); onSelect(null);
    setChain(null); setExpiries([]); setSelectedExpiry(null); setDefaultExpiry(null);
    if (!token) return;
    setLoading(true);
    axios.get(`${API_URL}/fo/bootstrap/${underlying}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        const d = r.data;
        const opts = d.option_expiries || [];
        const def  = d.selected_option_expiry || opts[0] || null;
        setExpiries(opts);
        setSelectedExpiry(def);
        setDefaultExpiry(def);
        setChain(normalizeChainData(d.option_chain));
      })
      .catch(() => setChain(null))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [underlying, token]);

  // Refetch chain when user switches to a non-default expiry
  useEffect(() => {
    if (!selectedExpiry || !defaultExpiry || selectedExpiry === defaultExpiry || !token) return;
    setChain(null); setSelected(null); onSelect(null);
    setLoading(true);
    axios.get(`${API_URL}/fo/chain/${underlying}`, {
      params: { expiry: selectedExpiry },
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setChain(normalizeChainData(r.data)))
      .catch(() => setChain(null))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExpiry]);

  // Click-outside closes dropdown
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = useMemo(() => {
    if (!chain?.chain) return [];
    if (!query.trim()) return chain.chain;
    return chain.chain.filter(r => String(r.strike).includes(query.trim()));
  }, [chain, query]);

  const pick = useCallback((row) => {
    setSelected(row); setQuery(''); setShowDrop(false); onSelect(row);
  }, [onSelect]);

  const clear = useCallback(() => {
    setSelected(null); onSelect(null);
  }, [onSelect]);

  const ltp = selected ? (optType === 'CE' ? selected.callLtp : selected.putLtp) : null;

  return (
    <div ref={ref} className="space-y-1.5">

      {/* Expiry pills */}
      {expiries.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[10px] font-medium flex-shrink-0" style={{ color: colors.secondaryText }}>Expiry:</span>
          {expiries.map(exp => {
            const active = exp === selectedExpiry;
            return (
              <button key={exp}
                onMouseDown={() => setSelectedExpiry(exp)}
                className="text-[10px] font-bold px-2 py-0.5 rounded-full transition-all"
                style={active
                  ? { backgroundColor: colors.accent, color: '#fff' }
                  : { backgroundColor: `${colors.accent}15`, color: colors.secondaryText, border: `1px solid ${colors.accent}25` }}>
                {fmtExp(exp)}
              </button>
            );
          })}
        </div>
      )}

      {/* Selected chip or search input */}
      <div className="relative">
        {selected ? (
          <div className="flex items-center justify-between px-2.5 py-2 rounded-lg"
            style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.accent}30` }}>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-xs font-black" style={{ color: colors.primaryText }}>{selected.strike}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                style={{ backgroundColor: `${ZONE_COLOR[selected.zone] || colors.accent}20`,
                         color: ZONE_COLOR[selected.zone] || colors.accent }}>
                {selected.zone}
              </span>
              <span className="text-xs font-semibold"
                style={{ color: optType === 'CE' ? '#16C784' : '#EF4444' }}>
                {optType} {ltp != null ? `₹${ltp}` : '…'}
              </span>
              {selectedExpiry && (
                <span className="text-[10px] opacity-50 truncate" style={{ color: colors.secondaryText }}>
                  {fmtExp(selectedExpiry)}
                </span>
              )}
            </div>
            <button onMouseDown={clear} className="flex-shrink-0 ml-2">
              <X className="w-3 h-3" style={{ color: colors.secondaryText }} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg"
            style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
            {loading
              ? <Loader className="w-3 h-3 animate-spin flex-shrink-0" style={{ color: colors.accent }} />
              : <Search className="w-3 h-3 flex-shrink-0" style={{ color: colors.secondaryText }} />}
            <input className="flex-1 bg-transparent text-xs outline-none" style={{ color: colors.primaryText }}
              placeholder={loading ? 'Loading strikes…' : 'Search strike e.g. 24500…'}
              value={query} disabled={loading}
              onChange={e => { setQuery(e.target.value); setShowDrop(true); }}
              onFocus={() => setShowDrop(true)} />
            {query && (
              <button onMouseDown={() => setQuery('')}>
                <X className="w-3 h-3" style={{ color: colors.secondaryText }} />
              </button>
            )}
          </div>
        )}

        {showDrop && !selected && filtered.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 rounded-lg overflow-hidden z-50 shadow-xl"
            style={{ backgroundColor: colors.cardBg || colors.inputBg, border: `1px solid ${colors.border}` }}>
            {chain?.spot && (
              <div className="px-3 py-1.5"
                style={{ borderBottom: `1px solid ${colors.border}`, backgroundColor: `${colors.accent}10` }}>
                <span className="text-[11px]" style={{ color: colors.secondaryText }}>
                  Spot: <span className="font-semibold" style={{ color: colors.primaryText }}>
                    ₹{Number(chain.spot).toLocaleString('en-IN')}
                  </span>
                  {selectedExpiry && <span className="ml-2 opacity-60">{fmtExp(selectedExpiry)}</span>}
                </span>
              </div>
            )}
            <div className="max-h-52 overflow-y-auto">
              {filtered.map(row => {
                const zc = ZONE_COLOR[row.zone] || colors.secondaryText;
                return (
                  <button key={row.strike} onMouseDown={() => pick(row)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:opacity-75 transition-opacity"
                    style={{ backgroundColor: colors.cardBg || colors.inputBg }}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-black" style={{ color: colors.primaryText }}>{row.strike}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                        style={{ backgroundColor: `${zc}20`, color: zc }}>{row.zone}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] font-semibold" style={{ color: '#16C784' }}>
                        CE {row.callLtp != null ? `₹${row.callLtp}` : '…'}
                      </span>
                      <span className="text-[11px] font-semibold" style={{ color: '#EF4444' }}>
                        PE {row.putLtp != null ? `₹${row.putLtp}` : '…'}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// -- Tier colours (matches CapitalWidget) -------------------------------------
const TIER_COLORS = {
  Elite: '#f59e0b', Advanced: '#6366f1', Standard: '#16C784',
  Restricted: '#f97316', Minimum: '#ef4444',
};

const CALC_SESSION_KEY = 'calc_session_v1';
const CALC_SESSION_TTL = 24 * 60 * 60 * 1000; // 24 hours

function loadSession() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(CALC_SESSION_KEY) || 'null');
    if (!raw) return null;
    if (Date.now() - (raw._savedAt || 0) > CALC_SESSION_TTL) {
      sessionStorage.removeItem(CALC_SESSION_KEY);
      return null;
    }
    return raw;
  } catch { return null; }
}
function saveSession(patch) {
  try {
    const prev = loadSession() || {};
    sessionStorage.setItem(CALC_SESSION_KEY, JSON.stringify({ ...prev, ...patch, _savedAt: Date.now() }));
  } catch {}
}

// -- Main calculator -----------------------------------------------------------
export default function PositionSizeCalculator({ profile, capitalData }) {
  const { colors } = useTheme();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const profileRiskPct = parseFloat(profile?.recommended_risk_percent) || 2;
  const engineRisk     = parseFloat(searchParams.get('risk')) || 0;
  const defCapital = capitalData?.active_capital
    ? String(Math.round(capitalData.active_capital))
    : '1000000';
  const defTrades  = Math.min(profile?.recommended_max_trades || 3, 10);

  const session = useMemo(() => loadSession(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [capital,   setCapital]   = useState(session?.capital   ?? defCapital);
  const [numTrades, setNumTrades] = useState(session?.numTrades ?? defTrades);
  const [riskPct,   setRiskPct]   = useState(session?.riskPct   ?? (engineRisk || profileRiskPct));
  const [allocs,    setAllocs]    = useState(() => session?.allocs ?? buildAllocs(session?.numTrades ?? defTrades));
  const [results,   setResults]   = useState({});

  // Persist top-level state on every change
  useEffect(() => { saveSession({ capital, numTrades, riskPct, allocs }); }, [capital, numTrades, riskPct, allocs]);

  useEffect(() => {
    if (capitalData?.active_capital) setCapital(String(Math.round(capitalData.active_capital)));
  }, [capitalData?.active_capital]);
  useEffect(() => {
    if (profile?.recommended_max_trades) setNumTrades(Math.min(profile.recommended_max_trades, 10));
    if (profile?.recommended_risk_percent && !engineRisk) setRiskPct(parseFloat(profile.recommended_risk_percent));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.recommended_max_trades, profile?.recommended_risk_percent]);

  useEffect(() => {
    // only reset allocs when numTrades changes AND there's no saved allocs for this count
    setAllocs(prev => prev.length === numTrades ? prev : buildAllocs(numTrades));
    setResults({});
  }, [numTrades]);

  const [slotStates,    setSlotStates]    = useState(() => session?.slotStates ?? {});
  const [slotResetKey, setSlotResetKey]  = useState(0);
  const [socialOpen,    setSocialOpen]    = useState(false);
  const [capturing,     setCapturing]     = useState(false);
  const riskCardRef = useRef(null);

  const handleSlotStateChange = useCallback((idx, state) => {
    setSlotStates(prev => {
      const next = { ...prev, [idx]: state };
      saveSession({ slotStates: next });
      return next;
    });
  }, []);

  const capitalN        = parseFloat(capital) || 0;
  const maxSlots        = profile?.recommended_max_trades || 5;
  const riskBudget      = capitalN * riskPct / 100;          // max loss per trade
  const totalRiskBudget = riskBudget * numTrades;
  const totalRiskPct    = riskPct * numTrades;
  const allocTotal      = allocs.reduce((s, v) => s + v, 0);

  const slotCapitals = allocs.map(p => Math.floor(capitalN * p / 100));

  const adjustAlloc = (idx, delta) => setAllocs(prev => applyAllocDelta(prev, idx, delta));

  const handleResult = useCallback((idx, data) => {
    setResults(prev => ({ ...prev, [idx]: data }));
  }, []);

  const handleSocialShare = async () => {
    if (!riskCardRef.current) return;
    setCapturing(true);
    try {
      await captureAndShare(riskCardRef.current, 'risk-plan.png');
    } catch (e) { console.error('Share failed', e); }
    finally { setCapturing(false); setSocialOpen(false); }
  };

  const filledSlots        = Object.values(results).filter(Boolean);
  const totalCapitalUsed   = filledSlots.reduce((s, r) => s + r.capitalUsed,    0);
  const totalActualLoss    = filledSlots.reduce((s, r) => s + r.actualMaxLoss,  0);
  const totalMaxProfit     = filledSlots.reduce((s, r) => s + r.maxProfit,      0);
  const totalNetProfit     = filledSlots.reduce((s, r) => s + r.netProfit,      0);
  const totalNetLoss       = filledSlots.reduce((s, r) => s + r.netLoss,        0);
  const totalBrokerage     = filledSlots.reduce((s, r) => s + r.brokerage,      0);
  const numFilled          = filledSlots.length;
  const anyHasSL           = filledSlots.some(r => r.actualMaxLoss > 0);
  const anyHasTarget       = filledSlots.some(r => r.maxProfit > 0);
  const capitalExceeded    = numFilled > 0 && totalCapitalUsed > capitalN;

  const tierColor = TIER_COLORS[capitalData?.tier] || colors.accent;

  return (
    <div className="space-y-3">

      {/* -- Widget: Capital limit + profile summary --------------------------- */}
      <div data-tour="calc-capital-summary" className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${colors.border}`, backgroundColor: colors.cardBg }}>
        {/* Header */}
        <div className="px-3 py-2.5" style={{ borderBottom: `1px solid ${colors.border}` }}>
          <div className="flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.accent }} />
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: colors.primaryText }}>Trade Calculator</span>
            {profile?.risk_profile_type && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>
                {profile.risk_profile_type}
              </span>
            )}
          </div>
          <p className="text-[10px] mt-0.5 ml-5" style={{ color: colors.secondaryText }}>Based on your risk profile</p>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-3" style={{ backgroundColor: colors.border, gap: 1 }}>
          {[
            { label: 'Daily limit',  value: capitalData?.active_capital ? inr(capitalData.active_capital) : '…', sub: 'from capital tier',  color: tierColor },
            { label: 'Risk / trade', value: `${profileRiskPct}%`,                                                sub: 'from risk profile', color: '#EF4444' },
            { label: 'Max trades',   value: profile?.recommended_max_trades || '…',                              sub: 'from risk profile', color: colors.accent },
          ].map(({ label, value, sub, color }) => (
            <div key={label} className="flex flex-col items-center justify-center py-3 px-2"
              style={{ backgroundColor: colors.cardBg }}>
              <p className="text-sm font-black tabular-nums leading-none" style={{ color }}>{value}</p>
              <p className="text-[10px] mt-0.5 font-medium" style={{ color: colors.secondaryText }}>{label}</p>
              <p className="text-[9px] mt-0.5 opacity-50" style={{ color: colors.secondaryText }}>{sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Phase 3 — Decision Engine risk pre-fill notice */}
      {engineRisk > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl"
          style={{ backgroundColor: `${colors.accent}08`, border: `1px solid ${colors.accent}25` }}>
          <Zap className="w-3 h-3 flex-shrink-0" style={{ color: colors.accent }} />
          <p className="text-[11px]" style={{ color: colors.accent }}>
            Risk pre-set by Decision Engine — {engineRisk}% / trade
          </p>
        </div>
      )}

      {/* -- Overview: plan inputs --------------------------------------------- */}
      <div data-tour="calc-overview" className="rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2"
          style={{ backgroundColor: `${colors.accent}10`, borderBottom: `1px solid ${colors.border}` }}>
          <div className="flex items-center gap-2">
            <Zap className="w-3.5 h-3.5" style={{ color: colors.accent }} />
            <span className="text-[11px] font-semibold uppercase tracking-wider"
              style={{ color: colors.secondaryText }}>Plan Setup</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px]" style={{ color: riskPct !== profileRiskPct ? '#F59E0B' : colors.secondaryText }}>
              {riskPct}% risk/trade{riskPct !== profileRiskPct ? ' ⚠' : ''}
            </span>
            <button
              onClick={() => {
                sessionStorage.removeItem(CALC_SESSION_KEY);
                const t = Math.min(profile?.recommended_max_trades || 3, 10);
                const r = parseFloat(profile?.recommended_risk_percent) || 2;
                const c = capitalData?.active_capital ? String(Math.round(capitalData.active_capital)) : '1000000';
                setCapital(c); setNumTrades(t); setRiskPct(r);
                setAllocs(buildAllocs(t)); setResults({}); setSlotStates({});
                setSlotResetKey(k => k + 1);
              }}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all active:scale-95 active:opacity-60"
              style={{ backgroundColor: colors.inputBg, color: colors.secondaryText, border: `1px solid ${colors.border}` }}
            >
              <RefreshCw className="w-2.5 h-2.5" /> Reset
            </button>
          </div>
        </div>

        <div className="p-3 space-y-3">
          {/* Row 1: account size + trades inline */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] mb-0.5 flex items-center gap-1" style={{ color: colors.secondaryText }}>
                Account size
                {capitalData?.active_capital && (
                  <span className="font-semibold" style={{ color: tierColor }}> daily limit</span>
                )}
              </label>
              <input type="number" value={capital} onChange={e => setCapital(e.target.value)}
                className="w-full px-2.5 py-1.5 rounded-lg text-xs font-bold outline-none"
                style={{ backgroundColor: colors.inputBg, border: `1px solid ${tierColor}40`, color: colors.primaryText }}
                placeholder="e.g. 150000" />
            </div>
            <div>
              <label className="text-[10px] mb-0.5 block" style={{ color: colors.secondaryText }}>
                Trades today
                {numTrades > maxSlots && <span className="ml-1" style={{ color: '#F59E0B' }}>⚠ max {maxSlots}</span>}
              </label>
              <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg"
                style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
                <button onClick={() => setNumTrades(t => Math.max(1, t - 1))}
                  className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${colors.accent}20` }}>
                  <Minus className="w-2.5 h-2.5" style={{ color: colors.accent }} />
                </button>
                <span className="flex-1 text-center text-sm font-black tabular-nums"
                  style={{ color: numTrades > maxSlots ? '#F59E0B' : colors.accent }}>{numTrades}</span>
                <button onClick={() => setNumTrades(t => Math.min(10, t + 1))}
                  className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${colors.accent}20` }}>
                  <Plus className="w-2.5 h-2.5" style={{ color: colors.accent }} />
                </button>
              </div>
            </div>
          </div>

          {/* Row 2: risk % stepper */}
          <div>
            <div className="flex items-center justify-between mb-0.5">
              <label className="text-[10px]" style={{ color: colors.secondaryText }}>Risk % per trade</label>
              {riskPct !== profileRiskPct
                ? <button onClick={() => setRiskPct(profileRiskPct)}
                    className="flex items-center gap-1 text-[10px]" style={{ color: colors.accent }}>
                    <RefreshCw className="w-2.5 h-2.5" /> Reset to {profileRiskPct}%
                  </button>
                : <span className="text-[10px]" style={{ color: '#16C784' }}>profile ✓</span>}
            </div>
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg"
              style={{ backgroundColor: colors.inputBg,
                       border: `1px solid ${riskPct !== profileRiskPct ? '#F59E0B40' : colors.border}` }}>
              <button onClick={() => setRiskPct(r => Math.max(0.5, parseFloat((r - 0.5).toFixed(1))))}
                className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${colors.accent}20` }}>
                <Minus className="w-3 h-3" style={{ color: colors.accent }} />
              </button>
              <span className="flex-1 text-center text-base font-black tabular-nums"
                style={{ color: riskPct !== profileRiskPct ? '#F59E0B' : colors.accent }}>
                {riskPct}%
              </span>
              <button onClick={() => setRiskPct(r => Math.min(10, parseFloat((r + 0.5).toFixed(1))))}
                className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${colors.accent}20` }}>
                <Plus className="w-3 h-3" style={{ color: colors.accent }} />
              </button>
            </div>
          </div>

          {/* Row 3: 3 metric chips */}
          {capitalN > 0 && (
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { label: 'Risk budget',  value: inr(riskBudget),                        color: '#EF4444' },
                { label: 'Total risk',   value: `${totalRiskPct.toFixed(0)}%`,           color: totalRiskPct > 10 ? '#EF4444' : '#F59E0B' },
                { label: 'Cap / trade',  value: inr(Math.floor(capitalN / numTrades)),   color: colors.primaryText },
              ].map(({ label, value, color }) => (
                <div key={label} className="flex flex-col items-center py-1.5 rounded-lg"
                  style={{ backgroundColor: colors.inputBg }}>
                  <p className="text-xs font-black" style={{ color }}>{value}</p>
                  <p className="text-[10px]" style={{ color: colors.secondaryText }}>{label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Allocation bars */}
          {capitalN > 0 && numTrades > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <p className="text-[10px]" style={{ color: colors.secondaryText }}>Allocation per trade</p>
                <p className="text-[10px] font-semibold"
                  style={{ color: allocTotal === 100 ? '#16C784' : '#EF4444' }}>
                  {allocTotal}% {allocTotal === 100 ? '✓' : `(${allocTotal > 100 ? '+' : ''}${allocTotal - 100}%)`}
                </p>
              </div>
              {allocs.map((p, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <span className="w-4 text-center text-[10px] font-bold flex-shrink-0"
                    style={{ color: colors.secondaryText }}>{i + 1}</span>
                  <div className="flex-1 h-1.5 rounded-full overflow-hidden flex-shrink-0"
                    style={{ backgroundColor: colors.border }}>
                    <div className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${p}%`, backgroundColor: colors.accent }} />
                  </div>
                  <span className="text-[10px] font-bold w-7 text-right flex-shrink-0"
                    style={{ color: colors.primaryText }}>{p}%</span>
                  <span className="text-[10px] w-14 text-right flex-shrink-0"
                    style={{ color: colors.primaryText }}>{inr(slotCapitals[i])}</span>
                  <div className="flex gap-0.5 flex-shrink-0">
                    <button onClick={() => adjustAlloc(i, -5)}
                      className="w-5 h-5 rounded flex items-center justify-center"
                      style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
                      <Minus className="w-2.5 h-2.5" style={{ color: colors.secondaryText }} />
                    </button>
                    <button onClick={() => adjustAlloc(i, 5)}
                      className="w-5 h-5 rounded flex items-center justify-center"
                      style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
                      <Plus className="w-2.5 h-2.5" style={{ color: colors.secondaryText }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* -- Trade slots ------------------------------------------------------- */}
      {capitalN > 0 && numTrades > 0 && (
        <div data-tour="calc-trade-slots" className="space-y-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider px-1"
            style={{ color: colors.secondaryText }}>Trades</p>
          {Array.from({ length: numTrades }).map((_, i) => (
            <TradeSlot key={`${slotResetKey}-${i}`} index={i}
              slotCapital={slotCapitals[i]}
              riskBudget={riskBudget}
              colors={colors}
              navigate={navigate}
              onResult={handleResult}
              initialSlotState={slotStates[i]}
              onSlotStateChange={handleSlotStateChange}
            />
          ))}
        </div>
      )}

      {/* Total capital exceeded */}
      {capitalExceeded && (
        <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl"
          style={{ backgroundColor: '#EF444412', border: '1px solid #EF444440' }}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#EF4444' }} />
          <div>
            <p className="text-xs font-semibold" style={{ color: '#EF4444' }}>
              Total capital deployed exceeds account size
            </p>
            <p className="text-[13px] mt-0.5" style={{ color: colors.secondaryText }}>
              {inr(totalCapitalUsed)} across {numFilled} trades — account is {inr(capitalN)}.
              Reduce shares on one or more trades.
            </p>
          </div>
        </div>
      )}

      {/* Consolidated summary */}
      {numFilled > 0 && (
        <div data-tour="calc-plan-summary" className="rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>
          <div className="app-card px-4 py-3 flex items-center justify-between"
            style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderBottom: `1px solid ${colors.border}` }}>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" style={{ color: colors.accent }} />
              <p className="text-sm font-bold" style={{ color: colors.primaryText }}>Plan Summary</p>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-[13px] px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${colors.accent}15`, color: colors.accent }}>
                {numFilled}/{numTrades} trades planned
              </p>
              <button
                onClick={() => setSocialOpen(true)}
                disabled={capturing}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold"
                style={{ background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)', opacity: capturing ? 0.6 : 1 }}>
                {capturing ? <Loader className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" />} Post
              </button>
            </div>
          </div>

          {anyHasSL && anyHasTarget && (
            <div className="grid grid-cols-2">
              <div className="p-5 flex flex-col items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #16C78418 0%, #16C78405 100%)',
                         borderRight: `1px solid ${colors.border}` }}>
                <div className="flex items-center gap-1 mb-1">
                  <TrendingUp className="w-3.5 h-3.5" style={{ color: '#16C784' }} />
                  <p className="text-[13px] font-semibold" style={{ color: '#16C784' }}>BEST CASE</p>
                </div>
                <p className="text-3xl font-black" style={{ color: '#16C784' }}>+{inr(totalNetProfit)}</p>
                <p className="text-[14px] mt-1 font-semibold" style={{ color: '#16C784' }}>
                  +{pct((totalNetProfit / capitalN) * 100)} on account
                </p>
                <p className="text-[12px] mt-0.5" style={{ color: colors.secondaryText }}>
                  all targets hit, after charges
                </p>
              </div>
              <div className="p-5 flex flex-col items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #EF444418 0%, #EF444405 100%)' }}>
                <div className="flex items-center gap-1 mb-1">
                  <TrendingDown className="w-3.5 h-3.5" style={{ color: '#EF4444' }} />
                  <p className="text-[13px] font-semibold" style={{ color: '#EF4444' }}>WORST CASE</p>
                </div>
                <p className="text-3xl font-black" style={{ color: '#EF4444' }}>-{inr(totalNetLoss)}</p>
                <p className="text-[14px] mt-1 font-semibold" style={{ color: '#EF4444' }}>
                  -{pct((totalNetLoss / capitalN) * 100)} on account
                </p>
                <p className="text-[12px] mt-0.5" style={{ color: colors.secondaryText }}>
                  all SLs hit, after charges
                </p>
              </div>
            </div>
          )}

          <div className="app-card px-4 py-3 space-y-2.5" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
            {anyHasSL && anyHasTarget && totalNetLoss > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[13px]" style={{ color: colors.secondaryText }}>Plan R:R</span>
                  <span className="text-[13px] font-bold"
                    style={{ color: totalNetProfit / totalNetLoss >= 2 ? '#16C784'
                      : totalNetProfit / totalNetLoss >= 1.5 ? '#F59E0B' : '#EF4444' }}>
                    1:{(totalNetProfit / totalNetLoss).toFixed(1)}
                    {totalNetProfit / totalNetLoss >= 2 ? ' — Good'
                      : totalNetProfit / totalNetLoss >= 1.5 ? ' — OK' : ' — Poor'}
                  </span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.inputBg }}>
                  <div className="h-full rounded-full"
                    style={{ width: `${Math.min(100, (totalNetProfit / (totalNetProfit + totalNetLoss)) * 100)}%`,
                             background: 'linear-gradient(90deg, #16C784, #6366F1)' }} />
                </div>
              </div>
            )}

            <div className="space-y-2 pt-1" style={{ borderTop: `1px solid ${colors.border}` }}>
              {[
                ['Total capital deployed',                      inr(totalCapitalUsed)],
                anyHasSL     && ['Total max loss (if all SLs hit)',    `-${inr(totalActualLoss)}`],
                anyHasTarget && ['Total max profit (if all targets hit)', `+${inr(totalMaxProfit)}`],
                [`Brokerage + taxes (${numFilled} trades)`,     `~${inr(totalBrokerage)}`],
                anyHasSL && anyHasTarget && ['Net profit (best case)',  `+${inr(totalNetProfit)}`],
                anyHasSL && anyHasTarget && ['Net loss (worst case)',   `-${inr(totalNetLoss)}`],
              ].filter(Boolean).map(([label, value]) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-[13px]" style={{ color: colors.secondaryText }}>{label}</span>
                  <span className="text-xs font-semibold"
                    style={{ color: value.startsWith('+') ? '#16C784'
                      : value.startsWith('-') ? '#EF4444' : colors.primaryText }}>
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hidden card for html2canvas */}
      <div style={{ position: 'fixed', left: -9999, top: 0, width: 1080, height: 1350, opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
        <RiskCalcShareCard cardRef={riskCardRef} snap={{
          capital: capitalN, numTrades, numFilled,
          totalCapitalUsed, totalNetProfit, totalNetLoss,
          totalActualLoss, totalBrokerage,
          tier: capitalData?.tier || null,
          riskProfile: profile?.risk_profile_type || null,
          anyHasSL, anyHasTarget,
          trades: Array.from({ length: numTrades }, (_, i) => {
            const r = results[i];
            const s = slotStates[i] || {};
            const slotLabel = s.stock?.symbol || s.query || `Trade ${i + 1}`;
            return r ? { ...r, label: r.label || slotLabel } : { label: slotLabel, _empty: true };
          }),
        }} />
      </div>

      {/* Social share bottom sheet */}
      {socialOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setSocialOpen(false)}>
          <div className="w-full max-w-sm rounded-t-2xl p-5 space-y-4" style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-[15px] font-bold" style={{ color: colors.primaryText }}>Share Risk Plan</p>
              <button onClick={() => setSocialOpen(false)}><X className="w-4 h-4" style={{ color: colors.secondaryText }} /></button>
            </div>
            <div style={{ width: '100%', height: Math.round(1350 * (300/1080)), overflow: 'hidden', borderRadius: 10 }}>
              <div style={{ width: 1080, height: 1350, transform: `scale(${300/1080})`, transformOrigin: 'top left' }}>
                <RiskCalcShareCard snap={{
                  capital: capitalN, numTrades, numFilled,
                  totalCapitalUsed, totalNetProfit, totalNetLoss,
                  totalActualLoss, totalBrokerage,
                  tier: capitalData?.tier || null,
                  riskProfile: profile?.risk_profile_type || null,
                  anyHasSL, anyHasTarget,
                  trades: Array.from({ length: numTrades }, (_, i) => {
                    const r = results[i];
                    const s = slotStates[i] || {};
                    const slotLabel = s.stock?.symbol || s.query || `Trade ${i + 1}`;
                    return r ? { ...r, label: r.label || slotLabel } : { label: slotLabel, _empty: true };
                  }),
                }} />
              </div>
            </div>
            <p className="text-[12px] text-center" style={{ color: colors.secondaryText }}>Opens share sheet — pick Instagram, WhatsApp, or save to camera roll.</p>
            <button onClick={handleSocialShare} disabled={capturing} className="w-full py-3 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2" style={{ background: colors.accent, color: '#fff', opacity: capturing ? 0.6 : 1 }}>
              {capturing ? <><Loader className="w-4 h-4 animate-spin" /> Preparing…</> : <><Share2 className="w-4 h-4" /> Share as Image</>}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
